import { useSearchParams } from 'react-router-dom';

import { JobPostingDetail, RecruitmentProcessDetail, SerialID } from '../types';
import useSearchCandidates, {
  QuestionFilter,
  UseSearchCandidatesOptions,
} from './useSearchCandidates';

type UseSearchCandidatesControllerOptions = {
  key?: string;
  jobPostingId?: JobPostingDetail['id'];
  enabled?: boolean;
  page?: number;
  functionId?: SerialID;
  pageSize?: number;
  status?: RecruitmentProcessDetail['status'];
  additionalQuestionsCompleteness?: 'MISSING' | 'FINALIZED';
};

const useSearchCandidatesController = (props: UseSearchCandidatesControllerOptions = {}) => {
  const [searchParams] = useSearchParams();

  const {
    key,
    jobPostingId,
    page,
    functionId,
    enabled,
    pageSize,
    status,
    additionalQuestionsCompleteness,
  } = props;

  return useSearchCandidates({
    key,
    search: searchParams.get('search') || undefined,
    attachmentSearch: searchParams.get('attachment_search') || undefined,
    addressContryIn: searchParams.getAll('addressContryIn').join(',') || undefined,
    addressStateIn: searchParams.getAll('addressStateIn').join(',') || undefined,
    addressCityIn: searchParams.getAll('addressCityIn').join(',') || undefined,
    updatedAtMax: searchParams.get('updatedAtMax') || undefined,
    updatedAtMin: searchParams.get('updatedAtMin') || undefined,
    createdAtMax: searchParams.get('createdAtMax') || undefined,
    createdAtMin: searchParams.get('createdAtMin') || undefined,
    matchMin: searchParams.get('match_min') || undefined,
    matchMax: searchParams.get('match_max') || undefined,
    rawPotentialMin: searchParams.get('raw_potential_min') || undefined,
    rawPotentialMax: searchParams.get('raw_potential_max') || undefined,
    ocpMin: searchParams.get('ocp_min') || undefined,
    ocpMax: searchParams.get('ocp_max') || undefined,
    logicMin: searchParams.get('logic_min') || undefined,
    logicMax: searchParams.get('logic_max') || undefined,
    sjtMin: searchParams.get('sjt_min') || undefined,
    sjtMax: searchParams.get('sjt_max') || undefined,
    motivationalMin: searchParams.get('motivational_min') || undefined,
    motivationalMax: searchParams.get('motivational_max') || undefined,
    operationalLogic: searchParams.get('operational_logic') || undefined,
    ageStart: searchParams.get('age_start')
      ? parseInt(searchParams.get('age_start') as string, 10)
      : undefined,
    ageEnd: searchParams.get('age_end')
      ? parseInt(searchParams.get('age_end') as string, 10)
      : undefined,
    salaryExpectationStart: searchParams.get('salary_expectation_start')
      ? parseInt(searchParams.get('salary_expectation_start') as string, 10)
      : undefined,
    salaryExpectationEnd: searchParams.get('salary_expectation_end')
      ? parseInt(searchParams.get('salary_expectation_end') as string, 10)
      : undefined,
    disabilities: searchParams.getAll('disabilities').join(',') || undefined,
    candidateSourceOptions: searchParams.getAll('candidateSourceOptions').join(',') || undefined,
    tags: searchParams.getAll('tags').join(',') || undefined,
    page: page || parseInt(searchParams.get('page') || '1', 10),
    pageSize: pageSize || parseInt(searchParams.get('pageSize') || '10', 10),
    enabled,
    stage: searchParams.get('stages') || undefined,
    jobPostingId,
    status: status || searchParams.get('status') || undefined,
    sort: searchParams.get('sort') || undefined,
    functionId:
      functionId || (searchParams.get('match') ? Number(searchParams.get('match')) : undefined),
    direction: searchParams.get('direction') === 'descend' ? '-' : '',
    testCompleteness:
      (searchParams.get('testCompleteness') as 'MISSING' | 'FINALIZED') || undefined,
    additionalQuestionsCompleteness:
      additionalQuestionsCompleteness ||
      (searchParams.get('additionalQuestionsCompleteness') as 'MISSING' | 'FINALIZED') ||
      undefined,
    questionFilters: Array.from(searchParams.entries())
      .filter(([key]) => /question\[\w+\]/.test(key))
      .reduce<QuestionFilter>((acc, [key, value]) => {
        const questionId = key.replace('question[', '').replace(']', '');

        if (!acc[questionId]) {
          acc[questionId] = [value];
          return acc;
        }
        acc[questionId].push(value);
        return acc;
      }, {}),
  } as UseSearchCandidatesOptions);
};

export default useSearchCandidatesController;
